import React, { FunctionComponent, useRef } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { Divider, Paper, Typography } from '@mui/material';

import { isTruss } from '@paragon/contract-library';
import { DataRow, getChildrenOfDataRow, ModelChildLoader } from '@paragon/layout-models';
import { hasTrussDesignPermission, isLoggedInUserEngineer } from '@paragon/login-tools';

import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { formatComponentDesignForCustomSchemas } from '../../../../store/selectors/getSortedComponentDesignsSelectors';
import { canCurrentUserSeeParapetLoads } from '../../../../store/selectors/parapetLoadSelectors';
import { useAppSelector } from '../../../hooks';
import { DesignClientGrid, GridMethods } from './DesignClientGrid';
import ButtonBar from './header-buttons';
import { registerTrussLoadingSpecificComponentDesignDataLoaders } from './loaders/additionalLoads';
import { registerBasicComponentDesignDataLoaders } from './loaders/basicComponentDesignData';
import { registerParapetLoadsLoader } from './loaders/parapetLoads';
import { registerTrussDesignerSpecificComponentDesignDataLoaders } from './loaders/trussDesignerComponentDesignData';
import { useJackGirderConnectionLoader } from './loaders/useJackGirderConnectionLoader';
import { useLoadCaseLoader } from './loaders/useLoadCaseLoader';
import { GridAutoFocusPlugin } from './row-management/GridAutoFocusPlugin';
import { getSchemaForComponentDesign } from './schemas/CustomTrussDesignSchema';
import { sanitizeForSearch } from './util/sanitizeForSearch';

interface Permissions {
  canSeeParapetLoads: boolean;
  hasTrussDesignPermission: boolean;
}

interface Props {
  gridHeight: number;
}

function useLoadersForComponentPanel(permissions: Permissions, searchTerm: string): ModelChildLoader {
  const loadCaseLoader = useLoadCaseLoader(searchTerm);
  const jackGirderConnectionLoader = useJackGirderConnectionLoader();

  const loaders = new ModelChildLoader();
  registerBasicComponentDesignDataLoaders(loaders, jackGirderConnectionLoader, searchTerm);
  registerTrussLoadingSpecificComponentDesignDataLoaders(loaders, loadCaseLoader);

  if (permissions.hasTrussDesignPermission) {
    registerTrussDesignerSpecificComponentDesignDataLoaders(loaders, searchTerm);
  }

  if (permissions.canSeeParapetLoads) {
    registerParapetLoadsLoader(loaders);
  }

  return loaders;
}

const getContainersForComponentPanel = createSelector(
  [hasTrussDesignPermission, canCurrentUserSeeParapetLoads, isLoggedInUserEngineer, getActiveComponentDesign],
  (hasTrussDesignPermission, canSeeParapetLoads, loggedInUserIsEngineer, activeComponentDesign) => {
    let containers = [
      'member',
      'platePair',
      'bearing',
      'run',
      'chase',
      'analogJoint',
      'analogLink',
      'panel',
      'splice',
      'endCondition',
      'jackGirderConnection',
      'hanger',
      'loadCase',
      'manualLoad',
      'movingLoad',
    ];

    if (hasTrussDesignPermission) {
      containers = containers.concat(['referenceLine']);
    }

    if (canSeeParapetLoads) {
      containers = containers.concat(['parapetLoad']);
    }

    if (loggedInUserIsEngineer || (activeComponentDesign && activeComponentDesign.component.gussets.length > 0)) {
      containers = containers.concat(['gusset']);
    }

    if (
      loggedInUserIsEngineer ||
      (activeComponentDesign &&
        isTruss(activeComponentDesign.component) &&
        activeComponentDesign.component.braceMembers.length > 0)
    ) {
      containers = containers.concat(['brace']);
    }

    return containers;
  },
);

const ComponentPanel: FunctionComponent<Props> = ({ gridHeight }) => {
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const containers = useAppSelector(getContainersForComponentPanel);

  const gridRef = useRef<GridMethods>(null);

  const searchTerm = ''; // searching is [temporarily?] disabled for this panel

  const permissions = {
    hasTrussDesignPermission: useAppSelector(hasTrussDesignPermission),
    canSeeParapetLoads: useAppSelector(canCurrentUserSeeParapetLoads),
  };

  const loaders = useLoadersForComponentPanel(permissions, sanitizeForSearch(searchTerm));

  if (!activeComponentDesign)
    return (
      <Paper elevation={0} variant="outlined" sx={{ marginTop: 1 }}>
        <Typography variant="body1" color="textSecondary" sx={{ margin: 2 }}>
          No component design selected.
        </Typography>
      </Paper>
    );

  const formattedComponentDesign = formatComponentDesignForCustomSchemas(activeComponentDesign);
  const componentDesignSchema = getSchemaForComponentDesign(formattedComponentDesign);

  const componentDesignRow = new DataRow(formattedComponentDesign, componentDesignSchema);
  const topLevelRows = getChildrenOfDataRow(componentDesignRow as DataRow<unknown>, { loaders, containers });

  return (
    <Paper elevation={0} variant="outlined">
      <ButtonBar
        panelContext="component"
        onFocusItemRequested={(item) => {
          gridRef.current?.focusRow(item);
        }}
      />
      <Divider />

      <DesignClientGrid
        ref={gridRef}
        topLevelRows={topLevelRows}
        loaders={loaders}
        containers={containers}
        height={gridHeight}
      />
      <GridAutoFocusPlugin gridRef={gridRef} />
    </Paper>
  );
};

export default ComponentPanel;
